import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ApiClientService, CachedValuesService, ToastService, RightPanelService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';
import { CustomerForm } from './create-and-update-customer-modal.model';
import { CustomerResponse } from '@services/api-client/responses';
import { Role } from 'app/enums';
import { Customer, User } from 'app/types';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { CustomFormModule } from '@shared/custom-form/custom-form.module';
import { SharedModule } from '@shared/shared.module';

@UntilDestroy()
@Component({
    selector: 'app-create-and-update-customer-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomFormModule, SharedModule],
    templateUrl: './create-and-update-customer-modal.component.html',
    styleUrl: './create-and-update-customer-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateAndUpdateCustomerModalComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly rightPanelService = inject(RightPanelService);

    @Input() public editCustomer?: Customer;

    public showPotentialValidationMessages = true;
    public loading: boolean = false;
    public customerForm: FormGroup<CustomerForm>;
    public roles = Role;
    public isEditMode = false;

    private readonly dataChangedSubject: BehaviorSubject<Customer | null> = new BehaviorSubject<Customer | null>(null);
    public dataChangedUser$: Observable<Customer> = this.dataChangedSubject.asObservable().pipe(
        filter((entity) => entity != null),
        map((entity) => entity)
    );

    public readonly users$: Observable<DropdownItem<string>[]> = this.cachedValuesService.users$.pipe(
        map((data) =>
            data.map((item: User) => {
                return {
                    value: item.id,
                    label: item.firstName + ' ' + item.lastName
                } as DropdownItem<string>;
            })
        )
    );

    public get f() {
        return this.customerForm.controls;
    }

    public ngOnInit() {
        const fb = new FormBuilder();
        this.customerForm = fb.nonNullable.group({
            name: [this.editCustomer?.name || '', [Validators.required]],
            organizationNo: [this.editCustomer?.organizationNo || '', [Validators.required]],
            address: [this.editCustomer?.address || '', [Validators.required]],
            zipCode: [this.editCustomer?.zipCode || '', [Validators.required]],
            city: [this.editCustomer?.city || '', [Validators.required]],
            country: [this.editCustomer?.country || '', [Validators.required]],
            responsibleId: [this.editCustomer?.responsibleId]
        });

        this.isEditMode = !this.editCustomer;
    }

    public get responsibleName$(): Observable<string> {
        return this.users$.pipe(
            map((users: DropdownItem<string>[]) => {
                const user = users.find((u: DropdownItem<string>) => u.value === this.editCustomer?.responsibleId);
                return user ? user.label : '-';
            })
        );
    }

    public isDisabled() {
        return this.loading || !this.customerForm.dirty;
    }

    public save() {
        if (this.customerForm.dirty) {
            this.showPotentialValidationMessages = true;

            if (this.customerForm.valid) {
                this.loading = true;
                this.changeDetectorRef.markForCheck();

                if (this.editCustomer) {
                    this.apiClientService
                        .updateCustomer$({
                            id: this.editCustomer.id,
                            name: this.f.name.value,
                            organizationNo: this.f.organizationNo.value,
                            address: this.f.address.value,
                            zipCode: this.f.zipCode.value,
                            city: this.f.city.value,
                            country: this.f.country.value,
                            responsibleId: this.f.responsibleId.value
                        })
                        .pipe(untilDestroyed(this), take(1))
                        .subscribe({
                            next: (response: CustomerResponse) => {
                                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                                const message = this.translateService.instant(
                                    'TOAST_MESSAGES.UPDATE_CUSTOMER_SUCCESS'
                                ) as string;
                                this.dataChangedSubject.next(response);
                                this.toastService.open(title, message, { type: 'success' });
                                this.cachedValuesService.invalidateCache();
                                this.closePanel();
                            },
                            error: () => {
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();
                                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                                const message = this.translateService.instant(
                                    'TOAST_MESSAGES.UPDATE_CUSTOMER_ERROR'
                                ) as string;
                                this.toastService.open(title, message, { type: 'error', progressBar: true });
                            }
                        });
                } else {
                    this.apiClientService
                        .createCustomer$({
                            name: this.f.name.value,
                            organizationNo: this.f.organizationNo.value,
                            address: this.f.address.value,
                            zipCode: this.f.zipCode.value,
                            city: this.f.city.value,
                            country: this.f.country.value,
                            responsibleId: this.f.responsibleId.value
                        })
                        .pipe(untilDestroyed(this), take(1))
                        .subscribe({
                            next: (response: CustomerResponse) => {
                                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                                const message = this.translateService.instant(
                                    'TOAST_MESSAGES.CREATE_CUSTOMER_SUCCESS'
                                ) as string;
                                this.dataChangedSubject.next(response);
                                this.toastService.open(title, message, { type: 'success' });
                                this.cachedValuesService.invalidateCache();
                                this.closePanel();
                            },
                            error: () => {
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();
                                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                                const message = this.translateService.instant(
                                    'TOAST_MESSAGES.CREATE_CUSTOMER_ERROR'
                                ) as string;
                                this.toastService.open(title, message, { type: 'error', progressBar: true });
                            }
                        });
                }
            }
        }
    }

    public closePanel() {
        this.rightPanelService.hide();
    }

    public enterEditMode() {
        this.isEditMode = true;
        this.changeDetectorRef.markForCheck();
    }

    public cancelEdit() {
        this.isEditMode = false;
        this.showPotentialValidationMessages = false;
        // Reset form to original values
        if (this.editCustomer) {
            this.customerForm.patchValue({
                name: this.editCustomer.name || '',
                organizationNo: this.editCustomer.organizationNo || '',
                address: this.editCustomer.address || '',
                zipCode: this.editCustomer.zipCode || '',
                city: this.editCustomer.city || '',
                country: this.editCustomer.country || '',
                responsibleId: this.editCustomer.responsibleId || ''
            });
            this.customerForm.markAsPristine();
        } else {
            this.closePanel();
        }
        this.changeDetectorRef.markForCheck();
    }

    public saveAndExitEdit() {
        this.save();
        // The save method will call closePanel() on success, but if we want to stay in readonly mode:
        // this.isEditMode = false;
    }
}
